import { attr, belongsTo } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import moment from 'moment-timezone';
import BaseModel from 'volta/models/base-model';
import { collectionCommand } from 'volta/utils/api';
import { getDuration } from 'volta/utils/date-utils';

import type { IResourceName } from 'volta/models/base-model';
import type { TJobType } from 'volta/models/constants/job-type';
import type Schedule from './schedule';
import type { JobStateOption, TJobHistoStatus } from './types/job-histo';

export const Commands = {
  CancelJob: 'CancelJob'
};

export const ResourceName: IResourceName = {
  singular: 'jobHisto',
  plural: 'jobHistos'
};

// Object with status as key and icon, color, and classNames as value
export const JobStatusMapping: Partial<Record<TJobHistoStatus, JobStateOption>> = {
  Error: {
    icon: 'close-circle',
    color: 'red',
    label: 'error'
  },
  Success: {
    icon: 'checkmark-circle',
    color: 'green',
    label: 'success'
  },
  Cancelled: {
    icon: 'ban-outline',
    label: 'cancelled',
    color: 'gray-3'
  },
  Pending: {
    icon: 'pause-circle-outline',
    color: 'blue',
    label: 'pending'
  },
  InProgress: {
    icon: 'sync',
    classNames: 'u-animation-spin',
    label: 'inProgress',
    color: 'purple'
  }
};

// Collection commands

export default class JobHisto extends BaseModel {
  static modelName = 'job-histo' as const;

  static cancelJob = collectionCommand<{ entityId: string }, void, typeof JobHisto>(
    Commands.CancelJob
  );

  @attr('string') jobType!: TJobType;
  @attr('date') startedAt?: Date;
  @attr('date') endedAt?: Date;
  @attr('boolean') isSuccess!: boolean;
  @attr('boolean') isCancelled!: boolean;
  @attr('boolean') inProgress!: boolean;
  @attr('string') status!: TJobHistoStatus;
  @attr('string') state?: string;
  @attr() result?: JSON;
  @attr('string') errorMsg?: string;
  @attr('string') scheduleId?: string;
  @attr('string') duration?: string;
  @attr('string') createdBy?: string;

  @belongsTo('schedule', { async: false, inverse: null }) schedule?: Schedule;

  get jobName() {
    return this.schedule?.description;
  }

  get formattedDuration() {
    return getDuration(this.startedAt, this.endedAt ?? moment.utc());
  }

  get formattedStartedAt() {
    return this.startedAt ? moment(this.startedAt).format('L HH:mm:ss') : undefined;
  }
  get formattedEndedAt() {
    return this.endedAt ? moment(this.endedAt).format('L HH:mm:ss') : undefined;
  }

  get formattedEndedAtfromNow() {
    return this.endedAt ? moment(this.endedAt).fromNow() : undefined;
  }

  [Type] = 'job-histo' as const;
}

export const JobHistoEvents = {
  JobCalculationPending: 'JobCalculationPending',
  JobCalculationStarted: 'JobCalculationStarted',
  JobCalculationFinished: 'JobCalculationFinished',
  JobCalculationFailed: 'JobCalculationFailed',
  JobCalculationCancelled: 'JobCalculationCancelled'
};
